import axios from "axios";
import Links from "../constants/Links";

/**
 * Internal Examiner API service for managing internal examiners
 * Internal examiners are existing lecturers assigned the INTERNAL_EXAMINER role
 * Only HODs can perform these operations
 */
const InternalExaminers = {
  /**
   * Assign internal examiner role to an existing lecturer
   * @param {number} lecturerId - Lecturer ID
   * @param {number} [department] - Department ID. Required for DEAN_SPGS; ignored for HOD.
   * @returns {Promise<object>}
   */
  assignRole: async (lecturerId, department) => {
    return axios.post(
      Links.internalExaminers.assignRole + lecturerId,
      null,
      department != null ? { params: { department } } : undefined
    );
  },

  /**
   * Remove internal examiner role from a lecturer (for a department)
   * @param {number} lecturerId - Lecturer ID
   * @param {number} [department] - Department ID. Required for DEAN_SPGS; ignored for HOD.
   * @returns {Promise<void>}
   */
  removeRole: async (lecturerId, department) => {
    return axios.delete(
      Links.internalExaminers.removeRole + lecturerId,
      department != null ? { params: { department } } : undefined
    );
  },

  /**
   * Get all internal examiners for HOD's department
   * @returns {Promise<object[]>}
   */
  getForDepartment: async () => {
    return axios.get(Links.internalExaminers.getForDepartment);
  },

  /**
   * Get internal examiners for a specific department
   * @param {number} departmentId - Department ID
   * @returns {Promise<object[]>}
   */
  getForSpecificDepartment: async (departmentId) => {
    return axios.get(Links.internalExaminers.getForSpecificDepartment + departmentId);
  },

  /**
   * Get eligible lecturers who can become internal examiners
   * Returns lecturers in HOD's department who don't yet have the INTERNAL_EXAMINER role
   * @returns {Promise<object[]>}
   */
  getEligibleLecturers: async (department) => {
    return axios.get(
      Links.internalExaminers.getEligibleLecturers,
      department != null ? { params: { department } } : undefined
    );
  },

  /**
   * Assign internal examiner to a student
   * @param {string} studentId - Student user ID
   * @param {number} lecturerId - Internal examiner lecturer ID
   * @returns {Promise<object>}
   */
  assignToStudent: async (studentId, lecturerId) => {
    return axios.post(Links.internalExaminers.assignToStudent, null, {
      params: {
        studentId,
        lecturerId
      }
    });
  },

  /**
   * Remove internal examiner from a student
   * @param {string} studentId - Student user ID
   * @returns {Promise<object>}
   */
  removeFromStudent: async (studentId) => {
    return axios.delete(Links.internalExaminers.removeFromStudent, {
      params: {
        studentId
      }
    });
  },

  /**
   * Get students assigned to a specific internal examiner
   * @param {number} lecturerId - Internal examiner lecturer ID
   * @returns {Promise<object[]>}
   */
  getStudentsForExaminer: async (lecturerId) => {
    return axios.get(Links.internalExaminers.getStudentsForExaminer + lecturerId);
  },

  /**
   * Get students assigned to the current internal examiner (self)
   * @returns {Promise<object[]>}
   */
  getMyStudents: async () => {
    return axios.get(Links.internalExaminers.getMyStudents);
  },

  generateMagicLink: async (lecturerId) => {
    return axios.post(
      Links.internalExaminers.generateMagicLink
        + lecturerId
        + Links.internalExaminers.magicLink
    );
  }
};

export default InternalExaminers;
